import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonOnError, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalInvestigationsRevokedItemModel } from 'forms/sections/legal/investigation/revoked/types';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import NotApplicable from 'components/Form/NotApplicable';
import { LegalInvestigationsInvestigatingAgencyModel } from 'forms/sections/legal/investigation/history/types';
import { LEGAL_INVESTIGATIONS_CLEARANCE_ISSUES } from 'config/formSections/legal';
import InvestigatingAgency from './InvestigatingAgency';

interface Props extends Partial<LegalInvestigationsRevokedItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  formType?: string
  prefix?: string
}

export default class RevokedItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalInvestigationsRevokedItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date,
      DateNotApplicable: this.props.DateNotApplicable,
      Agency: this.props.Agency,
      Explanation: this.props.Explanation,
      AgencyExplanation: this.props.AgencyExplanation,
      AgencyNotApplicable: this.props.AgencyNotApplicable,
      ...queue,
    });
  };

  updateDate = (values: LegalInvestigationsRevokedItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateDateNotApplicable = (
    values: LegalInvestigationsRevokedItemModel['DateNotApplicable']
  ) => {
    this.update({
      DateNotApplicable: values,
      Date: {}
    });
  };

  updateAgency = (values: LegalInvestigationsRevokedItemModel['Agency']) => {
    this.update({
      Agency: values,
    });
  };

  updateAgencyPvq = (values: Partial<LegalInvestigationsRevokedItemModel>) => {
    this.update({
      Agency: values.Agency,
      AgencyExplanation: values.Agency?.value === 'Other' ? this.props.AgencyExplanation : {},
    });
  };

  updateAgencyExplanation = (value: LegalInvestigationsRevokedItemModel['AgencyExplanation']) => {
    this.update({
      AgencyExplanation: value
    });
  };

  updateAgencyNotApplicable = (
    values: LegalInvestigationsRevokedItemModel['AgencyNotApplicable']
  ) => {
    this.update({
      AgencyNotApplicable: values,
      Agency: {} as LegalInvestigationsRevokedItemModel['Agency'],
      AgencyExplanation: {} as LegalInvestigationsRevokedItemModel['AgencyExplanation'],
    });
  };

  updateExplanation = (values: LegalInvestigationsRevokedItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div>
        <Field
          title={i18n.t(`${this.props.prefix}.revoked.heading.agency`)}
          adjustFor="big-buttons"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
          exactAttribute
        >
          <InvestigatingAgency
            {...{
              Agency: this.props.Agency,
              Explanation: this.props.AgencyExplanation,
            }}
            name="Agency"
            onUpdate={this.updateAgencyPvq}
            onError={this.props.onError}
            className="legal-investigations-history-agency"
            required={this.props.required && this.props.AgencyNotApplicable?.applicable}
            scrollIntoView={this.props.scrollIntoView}
            attribute={attribute}
            formType={this.props.formType}
          />
        </Field>

        {this.props.Agency?.value === 'Other'
                          && (
                          <Field
                            title={i18n.t('legal.pvq.investigations.history.heading.agencyexplanation')}
                            scrollIntoView={this.props.scrollIntoView}
                            attribute={`${attribute}.AgencyExplanation`}
                            exactAttribute
                          >
                            <Textarea
                              {...this.props.AgencyExplanation}
                              name="AgencyExplanation"
                              className="legal-investigations-revoked-agency-explanation"
                              onUpdate={this.updateAgencyExplanation}
                              onError={this.props.onError}
                              required={this.props.required}
                              disabled={this.props.disabled}
                              attribute={`${this.props.attribute}.AgencyExplanation`}
                            />
                          </Field>
                          )}

        <Field
          title={i18n.t(`${this.props.prefix}.revoked.heading.date`)}
          help={`${this.props.prefix}.revoked.help.date`}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
          exactAttribute
        >
          <NotApplicable
            {...this.props.DateNotApplicable}
            name="DateNotApplicable"
            className="legal-investigations-revoked-date-notapplicable"
            onUpdate={this.updateDateNotApplicable}
            onError={this.props.onError}
            or={i18n.m(`${this.props.prefix}.revoked.para.or`)}
            label={i18n.t(`${this.props.prefix}.revoked.label.idk`)}
            required={this.props.required}
            attribute={`${attribute}.DateNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Date)}
          >
            <DateControl
              {...this.props.Date}
              name="Date"
              onUpdate={this.updateDate}
              minDateEqualTo
              onError={this.props.onError}
              className="legal-investigations-revoked-date"
              required={this.props.required}
              hideDay
              attribute={`${attribute}.Date`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t(`${this.props.prefix}.revoked.heading.explanation`)}
          help={`${this.props.prefix}.revoked.help.explanation`}
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
          exactAttribute
        >
          <Textarea
            {...this.props.Explanation}
            name="Explanation"
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            className="legal-investigations-revoked-explanation"
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>
      </div>
    ) : (
      <div>
        <Field
          title={i18n.t('legal.investigations.revoked.heading.date')}
          help="legal.investigations.revoked.help.date"
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            onUpdate={this.updateDate}
            minDateEqualTo
            onError={this.props.onError}
            className="legal-investigations-revoked-date"
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('legal.investigations.revoked.heading.agency')}
          adjustFor="text"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Agency`}
        >
          <Text
            name="Agency"
            {...this.props.Agency}
            onUpdate={this.updateAgency}
            onError={this.props.onError}
            className="legal-investigations-revoked-agency"
            required={this.props.required}
            attribute={`${attribute}.Agency`}
          />
        </Field>

        <Field
          title={i18n.t('legal.investigations.revoked.heading.explanation')}
          help="legal.investigations.revoked.help.explanation"
          adjustFor="textarea"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Explanation`}
        >
          <Textarea
            name="Explanation"
            {...this.props.Explanation}
            onUpdate={this.updateExplanation}
            onError={this.props.onError}
            className="legal-investigations-revoked-explanation"
            required={this.props.required}
            attribute={`${attribute}.Explanation`}
          />
        </Field>
      </div>
    );
  }
}

RevokedItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
  AgencyExplanation: {},
  AgencyNotApplicable: { applicable: true },
  DateNotApplicable: { applicable: true },
};
